import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

export default function CustomerLogView({ cust_id, token }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  useEffect(() => {
    if (!cust_id || !token) return;

    const fetchLogs = async () => {
      try {
        setLoading(true);

        const response = await fetch(
          `https://api.wifikamu.my.id/act-log?cust_id=${cust_id}`,
          {
            headers: {
              Authorization: token,
            },
          }
        );

        const result = await response.json();

        if (result.success) {
          setLogs(result.data || []);
        }
      } catch (error) {
        console.error("Failed to fetch logs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [cust_id, token]);

  return (
    <>
      {/* HEADER */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-1.5 h-5 bg-orange-500 rounded-full"></div>
        <h2 className="text-xl font-bold dark:text-white">Activity Logs</h2>
      </div>

      {/* LOADING */}
      {loading && (
        <div className="p-6 text-center text-gray-500 dark:text-gray-400">
          Loading activity logs...
        </div>
      )}

      {/* EMPTY STATE */}
      {!loading && logs.length === 0 && (
        <div className="p-8 border-2 border-dashed border-gray-200 dark:border-gray-800 rounded-lg text-center">
          <p className="text-gray-500 dark:text-gray-400 font-medium">
            No activity logs recorded for this customer.
          </p>
        </div>
      )}

      {/* TABLE */}
      {!loading && logs.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="w-full overflow-x-auto"
        >
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-100 dark:bg-gray-900 text-gray-700 dark:text-gray-300">
                <th className="p-3 border-b dark:border-gray-700 text-sm">
                  ID
                </th>
                <th className="p-3 border-b dark:border-gray-700 text-sm">
                  Subject
                </th>
                <th className="p-3 border-b dark:border-gray-700 text-sm">
                  Action
                </th>
                <th className="p-3 border-b dark:border-gray-700 text-sm">
                  Description
                </th>
                <th className="p-3 border-b dark:border-gray-700 text-sm">
                  Admin
                </th>
                <th className="p-3 border-b dark:border-gray-700 text-sm">
                  Timestamp
                </th>
              </tr>
            </thead>

            <tbody>
              {logs.map((log) => (
                <tr
                  key={log.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 transition border-b dark:border-gray-800 last:border-0"
                >
                  <td className="p-3 text-sm font-mono text-gray-500 dark:text-gray-400">
                    #{log.id}
                  </td>

                  <td className="p-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {log.subject}
                  </td>

                  <td className="p-3">
                    <ActionBadge action={log.action} />
                  </td>

                  <td className="p-3 text-sm text-gray-600 dark:text-gray-400">
                    {log.description}
                  </td>

                  <td className="p-3 text-sm text-gray-600 dark:text-gray-400">
                    {log.admin_name}
                  </td>

                  <td className="p-3 text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(log.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      )}
    </>
  );
}

/* ===== Action Badge ===== */
function ActionBadge({ action }) {
  const base =
    "px-2.5 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase";

  const styles = {
    CREATE: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    UPDATE:
      "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
    DELETE: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
    ACTIVATE:
      "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  };

  const currentStyle =
    styles[action?.toUpperCase()] ||
    "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400";

  return <span className={`${base} ${currentStyle}`}>{action}</span>;
}
